import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const RequestPickup = () => {
   const [formData, setFormData] = useState({
      custId: sessionStorage.getItem('customer_id') || '',
      barcode: [],
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      country: '',
      address: '',
      city: '',
      borough: '',
      state: '',
      zip: '',
      estimatedBags: '0',
      schedule: 'One time',
      pickupDate: '',
      pickupTime: '',
      driverInstructions: '',
      paymentMethod: '',
      paymentFrequency: '',
      paypal_email: '',
      ach_account_name: '',
      ach_account_number: '',
      ach_routing_number: '',
      ach_account_type: '',
      stripe_account_id: ''
   });

   const [areaStatus, setAreaStatus] = useState({ message: '', valid: false });
   const [availableBarcodes, setAvailableBarcodes] = useState([]);

   const [availability, setAvailability] = useState({
      loading: false,
      valid: true,
      message: ""
   });
   const checkAvailability = async (date, time) => {
      if (!date || !time || !formData.tenant_id) return;

      try {
         setAvailability({ loading: true, valid: true, message: "" });

         const res = await axios.get("/auth/tenant-availability", {
            params: {
               tenant_id: formData.tenant_id,
               pickupDate: date,
               pickupTime: time
            }
         });

         if (!res.data.available) {
            setAvailability({
               loading: false,
               valid: false,
               message: res.data.message
            });
         } else {
            setAvailability({
               loading: false,
               valid: true,
               message: ""
            });
         }

      } catch (err) {
         setAvailability({
            loading: false,
            valid: false,
            message: "Unable to validate pickup availability."
         });
      }
   };


   const handleChange = (e) => {
      const { name, value, type, checked } = e.target;

      if (name === 'barcode') {
         const selected = Array.from(e.target.selectedOptions, option => option.value);
         setFormData((prevData) => ({
            ...prevData,
            barcode: selected,
            estimatedBags: String(selected.length)
         }));
      } else {
         setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
         if (name === 'borough' || name === 'zip') {
            setAreaStatus({ message: '', valid: false });
         }
      }

      if (name === "pickupDate") {
         setFormData(prev => ({ ...prev, pickupDate: value, pickupTime: "" }));
      }

      if (name === "pickupTime") {
         setFormData(prev => ({ ...prev, pickupTime: value }));
         checkAvailability(formData.pickupDate, value);
      }

   };

   useEffect(() => {
      if (formData.pickupDate && formData.pickupTime) {
         checkAvailability(formData.pickupDate, formData.pickupTime);
      }
   }, [formData.pickupDate]);


   const getTodayDate = () => {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
   };

   const generateTimeSlots = (selectedDate) => {
      const slots = [];
      const today = getTodayDate();
      const now = new Date();
      for (let hour = 8; hour <= 23; hour++) {
         for (let minutes of [0, 15, 30, 45]) {
            const time = new Date();
            time.setHours(hour, minutes, 0, 0);
            // If today → skip past times
            if (selectedDate === today && time < now) continue;
            // Format into 12-hour with AM/PM for both value & label
            const formatted = time.toLocaleTimeString('en-US', {
               hour: '2-digit',
               minute: '2-digit',
               hour12: true
            });
            slots.push({ value: formatted, label: formatted });
         }
      }
      return slots;
   };

   const [hasBarcodes, setHasBarcodes] = useState(true);
   const [submitting, setSubmitting] = useState(false);

   useEffect(() => {
      const fetchPickupData = async () => {
         if (formData.custId) {
            try {
               const res = await axios.get(`/auth/pickup-data`, {
                  params: { accountNumber: formData.custId }
               });
               const { user, barcodes } = res.data;
               setFormData(prev => ({
                  ...prev,
                  ...user, // fill user details from DB
               }));
               setAvailableBarcodes(barcodes);
               setHasBarcodes(barcodes.length > 0);
            } catch (err) {
               console.error('Failed to fetch pickup data:', err);
               toast.error('Could not load user or barcodes');
            }
         }
      };

      fetchPickupData();
   }, [formData.custId]);


   const handleSubmit = async (e) => {
      e.preventDefault();

      if (submitting) return; // 🔒 Prevent double submission
      setSubmitting(true);    // 🔒 Disable button immediately

      // 🔍 Payment method validations
      if (formData.paymentMethod === "PayPal") {
         if (!formData.paypal_email || formData.paypal_email.trim() === "") {
            toast.error("You have not yet added your PayPal email. Please add it in the My Account page before requesting a pickup.");
            return;
         }
      }

      if (formData.paymentMethod === "ACH") {
         if (
            !formData.ach_account_type ||
            !formData.ach_account_name ||
            !formData.ach_routing_number ||
            !formData.ach_account_number ||
            !formData.stripe_account_id ||
            formData.ach_account_type.trim() === "" ||
            formData.ach_account_name.trim() === "" ||
            formData.ach_routing_number.trim() === "" ||
            formData.ach_account_number.trim() === "" ||
            formData.stripe_account_id.trim() === ""
         ) {
            toast.error("You have not yet added your ACH payment details. Please add them in the My Account page before requesting a pickup.");
            return;
         }
      }

      // 🔍 Barcode check
      if (!formData.barcode.length) {
         toast.error('Please select at least one barcode.');
         return;
      }

      // 📦 Build payload dynamically based on payment method
      let paymentDetails = {};
      if (formData.paymentMethod === "PayPal") {
         paymentDetails = {
            paypal_email: formData.paypal_email
         };
      } else if (formData.paymentMethod === "ACH") {
         paymentDetails = {
            ach_account_type: formData.ach_account_type,
            ach_account_name: formData.ach_account_name,
            ach_routing_number: formData.ach_routing_number,
            ach_account_number: formData.ach_account_number,
            stripe_account_id: formData.stripe_account_id
         };
      }

      const payload = {
         ...formData,
         ...paymentDetails, // Only include relevant payment details
         barcode: formData.barcode.join(',')
      };

      try {
         const response = await fetch('/auth/request-pickup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
         });

         const result = await response.json();
         if (response.ok) {
            toast.success('Pickup request submitted successfully!');
            setFormData({
               ...formData,
               barcode: [],
               estimatedBags: '0',
               pickupDate: '',
               pickupTime: '',
               driverInstructions: ''
            });
            setTimeout(() => {
               window.location.reload();
            }, 1500);
            // 🔄 Remove the used barcodes locally
            setAvailableBarcodes(prev =>
               prev.filter(code => !formData.barcode.includes(code))
            );
         } else {
            toast.error(result.message || 'Submission failed.');
            setSubmitting(false); // allow retry
         }
      } catch (error) {
         console.error(error);
         toast.error('An error occurred during submission.');
         setSubmitting(false);
      }
   };

   return (
      <div>
         <div className="min-h-screen flex items-center justify-center">
            <div className="max-w-2xl mx-auto my-8 p-6 sm:p-6 md:p-8 bg-white rounded-lg shadow-[0_0_10px_0_#b2b2b2]">
               <div className="text-center mb-8">
                  <h1 className="text-center text-2xl font-semibold mb-4">
                     <span className=" text-[#9cc026]">Request a </span> Pickup
                  </h1>
                  {!hasBarcodes && (
                     <div className="bg-red-50 border border-red-300 text-red-700 px-6 py-5 rounded-md mb-6 text-center shadow-sm mt-6">
                        <p className="font-semibold mb-2">
                           You have no unused barcodes. Please order EcoBags before requesting a pickup.
                        </p>
                        <p className="text-sm mb-4">You can do so by filling out the request form below.</p>

                        <Link to="/request-bag"  // ⬅️ Update the path if needed
                           className="inline-block px-6 py-3 bg-[#54BE73] hover:bg-green-700 text-white font-semibold rounded-3xl transition">
                           Request Bag
                        </Link>
                     </div>
                  )}
                  <p className="mt-2 text-gray-600 text-base">
                     Select the barcode(s) from your EcoBags to request a pickup.
                  </p>
               </div>

               <form className={`space-y-6 ${!hasBarcodes ? 'pointer-events-none opacity-50 blur-sm' : ''}`}
                  onSubmit={handleSubmit}>
                  <div>
                     <label className="block text-sm font-medium mb-1">Select Available Barcode(s)</label>
                     <select name="barcode" multiple value={formData.barcode} required
                        onChange={handleChange} className="w-full p-3 rounded-md bg-green-50 border border-gray-300">
                        {availableBarcodes.map((code, idx) => (
                           <option key={idx} value={code}>{code}</option>
                        ))}
                     </select>
                     <p className="text-sm text-gray-500 mt-1">Hold Ctrl (or Cmd) to select multiple barcodes.</p>
                  </div>

                  <input type="hidden" name="custId" value={formData.custId} />
                  <input type="hidden" name="tenant_id" value={formData.tenant_id} />
                  <input type="hidden" name="paymentMethod" value={formData.paymentMethod} />
                  <input type="hidden" name="paymentFrequency" value={formData.paymentFrequency} />
                  <input type="hidden" name="paypal_email" value={formData.paypal_email} />
                  <input type="hidden" name="ach_account_name" value={formData.ach_account_name} />
                  <input type="hidden" name="ach_account_number" value={formData.ach_account_number} />
                  <input type="hidden" name="ach_routing_number" value={formData.ach_routing_number} />
                  <input type="hidden" name="ach_account_type" value={formData.ach_account_type} />
                  <input type="hidden" name="stripe_account_id" value={formData.stripe_account_id} />

                  <div>
                     <label className="block text-sm font-medium mb-1">Full Name <span className="text-red-500">*</span></label>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" placeholder="First Name" required />
                        <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" placeholder="Last Name" required />
                     </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     <div>
                        <label className="block text-sm font-medium mb-1">Email <span className="text-red-500">*</span></label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required />
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1">Phone Number <span className="text-red-500">*</span></label>
                        <input type="text" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required />
                     </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                     <div>
                        <label className="block text-sm font-medium mb-1">Country</label>
                        <select name="country" value={formData.country} onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required>
                           <option value="">Select Country</option>
                           <option value="Romania">Romania</option>
                           <option value="USA">USA</option>
                           <option value="India">India</option>
                        </select>
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1">Street Address</label>
                        <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required />
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1">City</label>
                        <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full p-2.5 rounded-md bg-green-50 border text-sm" placeholder="City" required />
                     </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                     <div>
                        <label className="block text-sm font-medium mb-1">State</label>
                        <input type="text" name="state" value={formData.state} onChange={handleChange}
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm cursor-not-allowed"
                           placeholder="State/Province" required readOnly />
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1">Borough</label>
                        <input type="text" name="borough" value={formData.borough} onChange={handleChange}
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm"
                           placeholder="ZIP" required readOnly />
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1">Zip</label>
                        <input type="text" name="zip" value={formData.zip} onChange={handleChange}
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm" placeholder="ZIP"
                           required readOnly />
                     </div>
                  </div>

                  {areaStatus.message && (
                     <p className={`text-sm mt-1 ${areaStatus.valid ? 'text-green-600' : 'text-red-600'}`}>
                        {areaStatus.message}
                     </p>
                  )}

                  <div>
                     <label className="block text-sm font-medium mb-1">
                        Estimate pickup amount.
                        <span className='font-semibold text-xs italic text-[#444]'> Actual items count will occur during processing at our facility.</span>
                     </label>
                     <select name="estimatedBags" value={formData.estimatedBags} disabled className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required>
                        {Array.from({ length: 50 }, (_, i) => (
                           <option key={i + 1} value={i + 1}>
                              {i + 1} EcoBag{i + 1 > 1 ? 's' : ''}
                           </option>
                        ))}
                     </select>
                  </div>

                  <div className="flex items-center gap-6">
                     <label className="text-sm inline-flex items-center gap-2">
                        <input type="radio" name="schedule" value="One time" checked={formData.schedule === 'One time'} onChange={handleChange} />
                        Schedule One Time Pickups
                     </label>
                     <label className="text-sm inline-flex items-center gap-2">
                        <input type="radio" name="schedule" value="Recurring" checked={formData.schedule === 'Recurring'} onChange={handleChange} />
                        Schedule Recurring Pickups
                     </label>
                  </div>


                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                     {/* Date Input */}
                     <div>
                        <label className="block text-sm font-medium mb-1">Pick a date</label>
                        <input type="date" name="pickupDate" value={formData.pickupDate}
                           onChange={handleChange} min={getTodayDate()} required
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm" />
                     </div>

                     {/* Time Input */}
                     <div>
                        <label className="block text-sm font-medium mb-1">Pick a time</label>
                        <select name="pickupTime" value={formData.pickupTime} onChange={handleChange}
                           className="w-full p-2.5 rounded-md bg-green-50 border text-sm" required>
                           <option value="">Select a time</option>
                           {generateTimeSlots(formData.pickupDate).map((slot, idx) => (
                              <option key={idx} value={slot.value}>{slot.label}</option>
                           ))}
                        </select>
                     </div>
                  </div>

                  {availability.message && (
                     <p className="text-sm mt-2 text-red-600 font-medium">
                        {availability.message}
                     </p>
                  )}


                  <div className="mb-4">
                     <label htmlFor="driverInstructions" className="block text-sm font-medium mb-1">Instructions for driver</label>
                     <textarea id="driverInstructions" name="driverInstructions" value={formData.driverInstructions}
                        onChange={handleChange} rows="4"
                        placeholder="e.g., Leave the bag at the front door, ring the bell, call before arriving..."
                        className="w-[350px] p-2.5 rounded-md bg-green-50 border text-sm h-[80px]">
                     </textarea>
                  </div>

                  <div className="flex flex-wrap justify-end gap-4">
                     <button
                        type="submit"
                        disabled={submitting || !availability.valid}
                        className={`px-4 py-2.5 font-semibold rounded-3xl transition
   ${submitting || !availability.valid
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-[#54BE73] hover:bg-green-700 text-white"
                           }`}
                     >
                        {submitting ? "Submitting..." : "SCHEDULE YOUR PICKUP"}
                     </button>
                  </div>
               </form>
            </div>
         </div>
         <ToastContainer position="top-right" autoClose={3000} />
      </div>
   );
};

export default RequestPickup;

